import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { UserBehaviorRisksAppWebComponentSchema, ColumnIndexes, ColumnKeys } from '../types';
import {
  WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
} from '../../../webcomponents.constants';
import { ColumnType } from '../../../grid/types/common';
import { UserBehaviorRisksGridDataProvider } from '../dataProvider/UserBehaviorRisksGridDataProvider';
import { translateFn } from '../../../utils/translator.helper';
import {
  companyRenderer,
  complianceRenderer,
  osRenderer,
  regionsRenderer,
  riskScoreRenderer,
} from '../../common/customRenderers';
import {
  changeEmptyStateIcon,
  getCompanyFilterUiConfig,
  getComplianceColumnValues,
  getMitigationTypeColumnValues,
  getOSTypeColumnValues,
  getStateColumnValues,
  getStatusColumnValues,
  getWatchlistColumnValues,
  setComplianceDisclaimer,
  processExternalFilters,
  setBulkActions,
  setGridCommonProperties,
  setTranslatedStrings,
} from '../../common/utils';
import { actionsColumnRenderer, affectedUsersRenderer, userBehaviorRiskRenderer } from './CustomGridRenderers';
import { GzPushBodyLayoutWebComponent } from '../../../ui/gzPushBodyLayout';
import { GridViewWebComponent } from '../../../grid/gridView';
import { convertGenericViewConfigToGridViewConfig } from '../../../grid/genericGridSmartViews/types';
import { GenericGridSmartViewsWebComponent } from '../../../grid/genericGridSmartViews';
import { ElementClass } from '../../../utils/htmlElement.utils';
import {
  getSavedSmartViewsCategory,
  setSmartViewsPanelSizeBasedOnState,
} from '../../../grid/genericGridSmartViews/utils';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { RemoteDetailsTypes } from '../../../filters/backendEnumFilter/types';
import { RiskTypes } from '../../common/constants';
import { TemplateState } from '../../../types/common/types';

export class UserBehaviorRisksAppView extends BaseWebComponentView {
  data: UserBehaviorRisksAppWebComponentSchema;
  public riskType = RiskTypes.USER_BEHAVIOUR_RISKS;
  public filterDependencyMapping = {
    companyId: ['affectedUsers', 'regions'],
  };

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('gz-push-body-layout .wrapper');
    this.dom.$title = this.shadow.querySelector('gz-push-body-layout .title');
    this.dom.$pageTitle = this.shadow.querySelector('.page_title');
    this.dom.$loader = this.shadow.querySelector('gz-push-body-layout .loader');

    this.dom.$pushBodyLayout = this.shadow.querySelector(
      WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT
    ) as GzPushBodyLayoutWebComponent;

    this.dom.$grid = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GRID_VIEW}`
    ) as GridViewWebComponent<ColumnIndexes, ColumnKeys>;

    this.dom.$loadingErrorMessage = this.shadow.querySelector('gz-push-body-layout .loading-error--message');
    this.dom.$gridSmartViews = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS}`
    ) as GenericGridSmartViewsWebComponent;
    this.dom.$smartViewsTitle = this.shadow.querySelector('gz-push-body-layout #smartViewsTitle');
    this.dom.$sideMenu = this.shadow.querySelector('gz-push-body-layout #sideMenu') as GzSideMenuWebComponent;
    this.dom.$riskManagementAdditionalText = this.shadow.querySelector('#riskManagementAdditionalText');
    this.dom.$scanEmptyStateButton = this.shadow.querySelector('#scan-button-empty-state');
    this.dom.$scanEmptyStateTooltip = this.shadow.querySelector('#scan-button-empty-state-tooltip');
    this.dom.$overlay = this.shadow.querySelector('#extra-details-context-menu');
    this.dom.$disclaimer = {
      $box: this.shadow.querySelector('#disclaimer-info-box'),
      $text: this.shadow.querySelector('#disclaimer-info-box-text'),
    };

    setBulkActions.call(this, {
      ignore: translateFn('Ignore risks'),
      restore: translateFn('Restore ignored risks'),
    });

    this.setTranslatedStrings();
    setComplianceDisclaimer(this.dom.$disclaimer);
  }

  setTranslatedStrings() {
    setTranslatedStrings.call(this);
    this.dom.$pageTitle.innerHTML = this.dependencies.translateFn('Identity risks');
  }

  buildSuccessView(data: UserBehaviorRisksAppWebComponentSchema) {
    this.data = data;
    this.buildSmartViews().then(async () => {
      if (data.externalFilters.length > 0) {
        await this.dom.$gridSmartViews.controller.setDefaultViewAsActiveView();
      }

      const activeView = this.dom.$gridSmartViews.controller.getActiveView();
      setSmartViewsPanelSizeBasedOnState(this.dom.$pushBodyLayout, this.dom.$gridSmartViews.smartViewState);
      await this.buildGrid(convertGenericViewConfigToGridViewConfig(activeView), data.externalFilters);
    });
  }

  buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  getTranslations() {
    return {
      smartViewsStrings: this.stringsTranslator.getSmartViewsTranslations(),
      defaultViewsTranslations: {
        all_user_behavior_risks: translateFn('All identity risks'),
        user_behavior_risks_high_severity: translateFn('High risk score'),
        user_behavior_risks_detected: translateFn('Detected'),
        user_behavior_risks_ignored: translateFn('Ignored'),
        watchlist_user_behavior_risks: this.dependencies.translateFn('Watchlist'),
      },
    };
  }

  async buildSmartViews() {
    this.dependencies.componentName = 'user-behavior-risks-grid';
    if (!(this.dom.$gridSmartViews instanceof GenericGridSmartViewsWebComponent)) {
      throw new Error('Missing grid smart views');
    }
    this.dom.$gridSmartViews.categoriesConfig = getSavedSmartViewsCategory(
      this.dependencies.componentName,
      this.getTranslations().defaultViewsTranslations,
      this.dependencies.api.clients.SmartViews.validateViewName
    );
    this.dom.$gridSmartViews.hasSearchInput = true;
    this.dom.$gridSmartViews.injectDependencies(this.dependencies);

    await this.dom.$gridSmartViews.start();
    this.removeElementClass(this.dom.$gridSmartViews, ElementClass.HIDDEN);
    this.setElementClass(this.dom.$loader, ElementClass.HIDDEN);
  }

  async buildGrid(view, externalFilters: Array<any> = []) {
    const {
      hasExternalFilters,
      initialViewWithoutExternalFilters,
      view: processedView,
    } = processExternalFilters(view, externalFilters);

    this.dom.$grid.propertiesSet = false;
    setGridCommonProperties(this.dom.$grid, processedView, hasExternalFilters, initialViewWithoutExternalFilters);
    this.dom.$grid.filtersUiConfigs = this.getFiltersUiConfig();
    this.dom.$grid.dataProvider = new UserBehaviorRisksGridDataProvider(this.dependencies);
    this.dom.$grid.allColumnDetails = this.getColumns();
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.COMPANY]: companyRenderer.bind(this),
      [ColumnKeys.RISK_SCORE]: riskScoreRenderer.bind(this),
      [ColumnKeys.COMPLIANCE]: complianceRenderer.bind(this),
      [ColumnKeys.USER_BEHAVIOR_RISK]: userBehaviorRiskRenderer.bind(this),
      [ColumnKeys.OS]: osRenderer.bind(this),
      [ColumnKeys.AFFECTED_USERS]: affectedUsersRenderer.bind(this),
      [ColumnKeys.SELECT]: actionsColumnRenderer.bind(this),
      [ColumnKeys.REGIONS]: regionsRenderer.bind(this),
    };
    this.dom.$grid.sideMenuParent = this.dom.$wrapper;
    const predefinedFiltersKeys = [
      ColumnKeys.USER_BEHAVIOR_RISK,
      ColumnKeys.RISK_SCORE,
      ColumnKeys.MITIGATION,
      ColumnKeys.AFFECTED_USERS,
    ];
    if (Application.Main.company.isPartner()) {
      predefinedFiltersKeys.unshift(ColumnKeys.COMPANY);
    }
    this.dom.$grid.predefinedFiltersKeys = predefinedFiltersKeys;
    this.dom.$grid.filterDependencyMapping = this.filterDependencyMapping;
    this.dom.$grid.propertiesSet = true;
    this.dom.$sideMenu.closeSideMenu();

    if (!this.dom.$grid.started) {
      this.dom.$grid.injectDependencies(this.dependencies);
      await this.dom.$grid.start();
    }

    changeEmptyStateIcon(this.dom.$grid);
    this.removeElementClass(this.dom.$header, ElementClass.HIDDEN);
  }

  getColumns() {
    const allColumns: any = [
      {
        column: ColumnIndexes.USER_BEHAVIOR_RISK,
        key: ColumnKeys.USER_BEHAVIOR_RISK,
        columnName: this.dependencies.translateFn('Identity risk'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        remoteDetails: {
          type: RemoteDetailsTypes.HumanBasedRisksNames,
          ignoredFieldsForFilters: ['label', 'type'],
        },
        removable: false,
      },
      {
        column: ColumnIndexes.RISK_SCORE,
        key: ColumnKeys.RISK_SCORE,
        columnName: translateFn('Risk score'),
        type: ColumnType.RANGE,
        removable: false,
        sortable: true,
      },
      {
        column: ColumnIndexes.MITIGATION,
        key: ColumnKeys.MITIGATION,
        columnName: translateFn('Mitigation type'),
        type: ColumnType.ENUM,
        values: getMitigationTypeColumnValues(),
        removable: true,
      },
      {
        column: ColumnIndexes.STATUS,
        key: ColumnKeys.STATUS,
        columnName: translateFn('Status'),
        type: ColumnType.ENUM,
        values: getStatusColumnValues(),
        removable: false,
      },
      {
        column: ColumnIndexes.STATE,
        key: ColumnKeys.STATE,
        columnName: translateFn('State'),
        type: ColumnType.ENUM,
        values: getStateColumnValues(),
        removable: false,
      },
      {
        column: ColumnIndexes.OS,
        key: ColumnKeys.OS,
        columnName: translateFn('Platform'),
        type: ColumnType.ENUM,
        values: getOSTypeColumnValues(),
        removable: true,
      },
      {
        column: ColumnIndexes.REGIONS,
        key: ColumnKeys.REGIONS,
        columnName: translateFn('Regions'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.UserBehaviorRisksRegions,
        },
      },
      {
        column: ColumnIndexes.COMPLIANCE,
        key: ColumnKeys.COMPLIANCE,
        columnName: translateFn('Compliance'),
        type: ColumnType.ENUM,
        values: getComplianceColumnValues(),
        removable: true,
      },
      {
        column: ColumnIndexes.AFFECTED_USERS,
        key: ColumnKeys.AFFECTED_USERS,
        columnName: translateFn('Vulnerable identities'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        removable: true,
        remoteDetails: {
          type: RemoteDetailsTypes.UserBehaviorRisksAffectedUsers,
        },
      },
      {
        column: ColumnIndexes.WATCHLIST,
        key: ColumnKeys.WATCHLIST,
        columnName: this.dependencies.translateFn('In watchlist'),
        type: ColumnType.ENUM,
        values: getWatchlistColumnValues(),
        width: '100px',
        removable: true,
        sortable: false,
      },
      {
        column: ColumnIndexes.SELECT,
        key: ColumnKeys.SELECT,
        columnName: '',
        type: ColumnType.ACTION,
        removable: false,
        sortable: false,
        width: '100px',
        filteringDisabled: true,
      },
    ];

    if (Application.Main.company.isPartner()) {
      allColumns.unshift({
        column: ColumnIndexes.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: this.dependencies.translateFn('Company'),
        type: ColumnType.REMOTE_SELECT,
        removable: true,
        filterResetValue: {
          exactMatch: Application.Main.user.company.id,
        },
      });
    }

    return allColumns;
  }

  private getFiltersUiConfig() {
    return [
      getCompanyFilterUiConfig(ColumnKeys.COMPANY),
      {
        key: ColumnKeys.USER_BEHAVIOR_RISK,
        config: {
          filterLabel: this.dependencies.translateFn('Identity risk name'),
        },
      },
    ];
  }
}
